import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBomb} from "@fortawesome/pro-solid-svg-icons/faBomb";
import {faRectangleHistory} from "@fortawesome/pro-solid-svg-icons/faRectangleHistory";
import {
    FlexAlign, FlexGap,
    FlexJustify,
    InfiniteLoadTrigger, ItemRowHeader,
    StackedAddonPageHeader,
    StackVertical,
    useLayout
} from "@twigsby/frontend-framework";
import {useTranslation} from "@twigsby/frontend-framework";
import {EndpointError} from "../../EndpointError";
import SendsHistoryItem from "../SendsHistoryItem";
import React, {useEffect, useState} from "react";
import SendsHistoryFilterControl, {SendHistoryFilterButton} from "./SendsHistoryFilterControl";
import {useSendMailHistory} from "../../../Providers/SendMailHistoryProvider";
import {GetItemCounts, SendsHistoryEntryType} from "@twigsby/client-data";

export type SendsHistoryControlType = {
    hideHeader?: boolean;
    forceHeader?: boolean;
    linkToSendsItem?: boolean;
    containerRef?: any;
}

const SendsHistoryControl = (props: SendsHistoryControlType) => {
    const { t } = useTranslation();
    const { isTiny, isSmall, isMedium, isLarge } = useLayout();
    const { pageId, userId, status, data, error, isFetchingNextPage, fetchNextPage, hasNextPage }  = useSendMailHistory();

    const [itemCounts, setItemCounts] = useState<any>({ loaded: 0, total: 0 } );

    const ItemPlaceholder = () => {
        return (
            <div className={'content-loader content-fader'} style={{ display: 'grid', alignItems: 'center', gap: 'var(--sm-unit)', margin: isMedium ? '0 var(--md-unit)' : 'var(--sm-unit)', gridTemplateColumns: isMedium ? `6em repeat(${!!userId || !!pageId ? '4' : '5'}, 1fr)` : 'var(--thumbnail-size) 1fr'}}>
                {isMedium ? (
                    <>
                        <div style={{ width: 'var(--thumbnail-size)', aspectRatio: 1 }} />
                        {!pageId && (
                            <div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />
                        )}
                        {!userId && (
                            <div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />
                        )}
                        <div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />
                        <div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />
                        <div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />
                    </>
                ) : (
                    <>
                        <div style={{ width: 'var(--thumbnail-size)', aspectRatio: 1 }} />
                        <div style={{ width: 'auto', height: isTiny ? '8em' : isSmall ? '7em' : '6em' }} />
                    </>
                )}
            </div>
        )
    }

    useEffect(() => {
        if(data) {
            setItemCounts(GetItemCounts(data));
        }
    }, [data]);

    return (
        <div className={`sends-history-rows ${props.hideHeader ? 'm-n-md rounded' : ''}`} style={{ overflow: props.hideHeader ? 'hidden' : 'auto' }}>
            {!props.hideHeader && (isLarge || props.forceHeader) && (
                <StackedAddonPageHeader
                    title={t('sends.profilesHistory.title')}
                    postTitleAddon={<SendHistoryFilterButton />}
                    content={<SendsHistoryFilterControl />}
                />
            )}

            {isMedium && (
                <ItemRowHeader sticky={true} extraClass={'pl-lg'}>
                    <div>{t('sends.profilesHistory.headings.file')}</div>
                    {!pageId && (
                        <div>{t('sends.profilesHistory.headings.pageId')}</div>
                    )}
                    {!userId && (
                        <div>{t('sends.profilesHistory.headings.user')}</div>
                    )}
                    <div>{t('sends.profilesHistory.headings.address')}</div>
                    <div>{t('sends.profilesHistory.headings.postage')}</div>
                    <div>{t('sends.profilesHistory.headings.date')}</div>
                </ItemRowHeader>
            )}

            {status === "loading" ? (
                <div className={'content-fader'}>
                    <ItemPlaceholder/>
                    <ItemPlaceholder/>
                    <ItemPlaceholder/>
                    <ItemPlaceholder/>
                    <ItemPlaceholder/>
                    <ItemPlaceholder/>
                    <ItemPlaceholder/>
                </div>
            ) : status === "error" ? (
                <EndpointError {...error} stacked={true} extraClass={'m-md'} />
            ) : (
                <>
                    {data && data.pages.map((group: any, groupIndex: number) =>
                        group.data.map((entry: SendsHistoryEntryType, index: number) =>
                            <SendsHistoryItem key={`sendsHistory${index}${groupIndex}`} {...entry} linkToSend={props.linkToSendsItem} hidePageDetails={!!pageId} hideUserDetails={!!userId} />
                        )
                    )}

                    {!data && (
                        <StackVertical gap={FlexGap.ExtraSmall} align={FlexAlign.Center} justify={FlexJustify.Center} extraClass={'m-xxl p-xxl text-center'}>
                            <FontAwesomeIcon icon={faRectangleHistory} size={"3x"} />
                            <h6 className={'sub-heading mt-sm mb-xs'}>{t('sends.profilesHistory.noData.title')}</h6>
                            <p>{t('sends.profilesHistory.noData.description')}</p>
                        </StackVertical>
                    )}

                    {itemCounts.total === 0 && (
                        <StackVertical gap={FlexGap.ExtraSmall} align={FlexAlign.Center} justify={FlexJustify.Center} extraClass={'m-xxl p-xxl text-center'}>
                            <FontAwesomeIcon icon={faRectangleHistory} size={"3x"} />
                            <h6 className={'sub-heading mt-sm mb-xs'}>{t('sends.profilesHistory.noResults.title')}</h6>
                        </StackVertical>
                    )}

                    {itemCounts.total > 0 && (
                        <InfiniteLoadTrigger
                            loaded={itemCounts.loaded}
                            totalCount={itemCounts.total}
                            hasNextPage={hasNextPage}
                            fetchNextPage={fetchNextPage}
                            isFetchingNextPage={isFetchingNextPage}
                        />
                    )}
                </>
            )}
        </div>
    )
}

export default SendsHistoryControl
